import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import { RiListSettingsLine } from 'react-icons/ri';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { space, color, border } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import filtersFromQuery from 'lib/filters-from-query';
import { useSelector, useDispatch } from 'react-redux';
import {
  changeOthersFilter,
  changeTermsFilter,
  resetFilters,
  changeFilters,
  reloadFilters,
  comparedToRequest,
  changeDataTypeFilter,
} from 'redux-api/action/filters';
import { MdSave, MdOutlineAdminPanelSettings, MdHelpOutline } from 'react-icons/md';
import { selectByFiler, selectOtherFilter } from 'redux-api/reselect/config';
import selectProfile from 'redux-api/reselect/profile';
import selectFilters from 'redux-api/reselect/filters';
import { useOutsideClick } from 'hooks';
import ButtonBorder from 'theme/buttons/btn-border';
import BtnGhost from 'theme/buttons/ghost';
import Tune from 'theme/icons/tune';
import Text from 'theme/text';
import ToolTip from 'theme/tooltip';
import Reload from 'theme/icons/reload';
import IconOptions from 'theme/icons/options';
import { Switch } from 'antd';
import translations from 'translations';
import MoreFilters from './more-filters';
import DateSelector from './date-selector';
import ManagePreset from './manage-preset';
import SavePreset from './save-preset';
import Tag from './tag';

const Wrapper = styled.div`
  padding: ${themeGet('space.m')}px ${themeGet('space.s')}px 0;

  @media (min-width: ${themeGet('breakpoints.0')}) {
    padding: ${themeGet('space.l')}px ${themeGet('space.l')}px 0;
  }

  position: ${({ isFixed }) => (isFixed ? 'fixed' : 'initial')};
  z-index: ${({ isFixed }) => (isFixed ? 1 : 'initial')};
  background: ${({ isFixed }) => (isFixed ? 'white' : 'initial')};
  min-height: ${({ isFixed }) => (isFixed ? '110px' : 'initial')};
  padding-bottom: ${({ isFixed }) => (isFixed ? '10px !important' : 'initial')};
  top: ${({ isFixed }) => (isFixed ? 0 : 'initial')};
  border-bottom: ${({ isFixed }) => (isFixed ? '1px solid #0a8ea0' : 'initial')};

  & > .row {
    display: flex;
    /* On a phone the two halves are one under the other and every control
       wraps: side by side, the buttons ran off the right edge of the screen. */
    flex-direction: column;
    justify-content: space-between;

    & > div {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-start;
      gap: ${themeGet('space.xs')}px;
    }

    & > .left {
      flex: 1;
    }

    & > .right {
      align-items: flex-start;
    }

    @media (min-width: ${themeGet('breakpoints.0')}) {
      flex-direction: row;

      & > div {
        flex-wrap: nowrap;
        gap: 0;
      }
    }
  }
`;

/* The period line and the explanation of what the charts show: useful on a
   desktop, three quarters of a phone screen before the first number. */
const OnlyWide = styled.div`
  display: none;

  @media (min-width: ${themeGet('breakpoints.0')}) {
    display: block;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;

  & > .column {
    display: flex;
    flex-direction: column;
    margin-right: ${themeGet('space.s')}px;

    & > .flex {
      display: flex;
      align-items: center;
    }
  }
`;

const FilterTags = styled.div`
  ${space}

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Menu = styled.div`
  ${space}
  ${color}

  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};

  width: 250px;
  position: absolute;
  right: 30px;
  margin-top: 54px;
  border: 1px solid;
  border-radius: 12px;
  z-index: 999;
  overflow: hidden;

  ${border}

  & > ul {
    width: 100%;
    margin: 0;
    padding: 0;

    & > li {
      width: 100%;
      height: 54px;
      list-style-type: none;
      display: flex;
      padding: 0 8px;
      box-shadow: inset 0px -1px 0px ${themeGet('colors.primary_100')};

      &:hover {
        box-shadow: inset 0px -2px 0px ${themeGet('colors.primary_200')};
      }

      &:active {
        box-shadow: inset 0px -4px 0px ${themeGet('colors.primary_500')};
      }
    }
  }
`;

const StyledBtnGhost = styled(BtnGhost)`
  width: 100%;

  & > span {
    justify-content: flex-start;
  }
`;

const StyledDivGhost = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 ${themeGet('space.xs')}px;
  min-height: 54px;

  & > span {
    justify-content: flex-start;
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: ${themeGet('colors.primary_400')};
  margin-left: ${themeGet('space.xxs')}px;
`;

function Filters({ noShowCompare, noReload }) {
  const { pathname, query, push } = useRouter();
  const dispatch = useDispatch();
  const ref = useRef();

  const [isOpenMoreFilters, setOpenMoreFilters] = useState(false);
  const [isManagePresetVisible, setManagePresetVisible] = useState(false);
  const [isSavePresetVisible, setSavePresetVisible] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [isOpen, setOpen] = useState(false);

  useOutsideClick(ref, () => {
    setOpen(false);
  });

  const handleScroll = () => {
    const offset = window.scrollY;
    const fixedPosition = 240;
    const minHeight = 900;
    // The theme's first breakpoint. Below it the page already has a sticky bar
    // of its own, and a second one 110px tall — on a screen this narrow — buried
    // the page under its own chrome.
    const minWidth = 921;

    if (offset > fixedPosition && window.innerHeight > minHeight && window.innerWidth >= minWidth) {
      setIsFixed(true);
    } else {
      setIsFixed(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    // Rotating a phone changes both measurements, and without this the bar
    // stayed in whatever state the last scroll left it in.
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const {
    isAdminAnalytics,
    payload: { analytics_type },
  } = useSelector(selectProfile);

  const { compare, dateRange, dates, others, terms, includeRepartitionCharge } = useSelector(
    (state) => state.filters,
  );

  const {
    dataType,
    isChanged,
    dateTo,
    dateFrom,
    filters,
    terms: termsFilter,
    filtersData,
    termsData,
  } = useSelector(selectFilters);

  const { languageCode, formatDate } = useSelector((state) => state.config);
  const selectByFilter = useSelector(selectByFiler);
  const otherFilter = useSelector(selectOtherFilter);

  useEffect(() => {
    const isQuery = Object.keys(query).length > 0;

    if (!query.accessToken && isQuery && !isChanged) {
      const parsedQuery = filtersFromQuery(query);

      const {
        dateFrom: dateFromQuery,
        dateTo: dateToQuery,
        others: othersParsed,
        terms: termsParsed,
      } = parsedQuery;

      let parsedDateToQuery = dateToQuery;

      if (moment(dateToQuery, formatDate).isAfter(moment())) {
        parsedDateToQuery = moment().format(formatDate);
      }

      dispatch(
        changeFilters({
          ...parsedQuery,
          dates: [moment(dateFromQuery, formatDate), moment(parsedDateToQuery, formatDate)],
          others: othersParsed,
          terms: termsParsed,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isChanged) {
      push({
        pathname,
        query: {
          compare: encodeURI(compare),
          dataType: encodeURI(dataType),
          dateRange: encodeURI(dateRange),
          dateFrom: encodeURI(dateFrom),
          dateTo: encodeURI(dateTo),
          filters: encodeURI(filters),
          terms: encodeURI(termsFilter),
          filtersData: encodeURI(filtersData),
          termsData: encodeURI(termsData),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChanged, filters, termsFilter, dateTo, dateFrom, compare, dataType, dateRange]);

  const {
    compareTo: { dateFrom: dateFromCompare, dateTo: dateToCompare },
  } = useSelector((state) => state.filters);

  useEffect(() => {
    if (noShowCompare) return;

    dispatch(
      comparedToRequest({
        compare,
        dateFrom: moment(dateFrom).format(formatDate),
        dateTo: moment(dateTo).format(formatDate),
        dateRange,
      }),
    );
  }, [noShowCompare, formatDate, dateFrom, dateTo, dispatch, compare, dateRange]);

  const formatComparedDates = useMemo(() => {
    if (noShowCompare) return null;
    moment.locale(languageCode);

    if (!dateFromCompare || !dateToCompare) return null;

    return {
      dateFrom: moment(dateFromCompare).format('DD MMM YYYY'),
      dateTo: moment(dateToCompare).format('DD MMM YYYY'),
    };
  }, [noShowCompare, dateFromCompare, dateToCompare, languageCode]);

  const handleReset = useCallback(() => {
    push({
      pathname,
      query: {},
    });

    dispatch(resetFilters());
  }, [dispatch, push, pathname]);

  const handleOpenMoreFilters = useCallback(() => {
    setOpenMoreFilters(!isOpenMoreFilters);
  }, [isOpenMoreFilters]);

  const handlePreset = useCallback(() => {
    setManagePresetVisible(true);
  }, []);

  const handleCloseManagePreset = useCallback(() => {
    setManagePresetVisible(false);
  }, []);

  const handleSavePreset = useCallback(() => {
    setSavePresetVisible(true);
  }, []);

  const handleCloseSavePreset = useCallback(() => {
    setSavePresetVisible(false);
  }, []);

  const handleChangeOtherFilter = useCallback(
    (type, label, value, filterColor, isExclusive = false) => {
      const filtersCopy = {
        ...others,
      };

      if (type === 'isbn' && value.trim().match(/.* .*/)) {
        const isbnValue = value.split('=')[1];
        const isbns = isbnValue.split(' ');
        isbns.forEach((isbn) => {
          filtersCopy[JSON.stringify(`${type}-${isbn}`)] = {
            type,
            label: isbn,
            value: `isbn=${isbn}`,
            color: filterColor,
            isExclusive,
          };
        });
      } else {
        filtersCopy[JSON.stringify(`${type}-${value}`)] = {
          type,
          label,
          value,
          color: filterColor,
          isExclusive,
        };
      }

      dispatch(changeOthersFilter(filtersCopy));
    },
    [dispatch, others],
  );

  const handleChangeTermFilter = useCallback(
    (type, label, value, filterColor, isExclusive = false) => {
      dispatch(
        changeTermsFilter({
          ...terms,
          [JSON.stringify(`${type}-${value}`)]: {
            type,
            label,
            value,
            color: filterColor,
            isExclusive,
          },
        }),
      );
    },
    [dispatch, terms],
  );

  const handleDelete = useCallback(
    (tagId) => {
      const othersFilter = { ...others };
      delete othersFilter[tagId];

      dispatch(changeOthersFilter(othersFilter));
    },
    [dispatch, others],
  );

  const handleDeleteTerm = useCallback(
    (tagId) => {
      const termsF = { ...terms };
      delete termsF[tagId];

      dispatch(changeTermsFilter(termsF));
    },
    [dispatch, terms],
  );

  const handleToggleExclusive = useCallback(
    (tagId) => {
      // Trovo il filtro da cambiare
      if (others[tagId]) {
        const filter = others[tagId];
        const filtersCopy = { ...others };

        // Cambio lo stato esclusivo
        filtersCopy[tagId] = {
          ...filter,
          isExclusive: !filter.isExclusive,
        };

        dispatch(changeOthersFilter(filtersCopy));
      } else if (terms[tagId]) {
        const term = terms[tagId];
        const termsCopy = { ...terms };

        // Cambio lo stato esclusivo
        termsCopy[tagId] = {
          ...term,
          isExclusive: !term.isExclusive,
        };

        dispatch(changeTermsFilter(termsCopy));
      }
    },
    [dispatch, others, terms],
  );

  const handleReload = useCallback(() => {
    dispatch(reloadFilters());
  }, [dispatch]);

  const handleChangeDataType = useCallback(
    (payload) => {
      dispatch(changeDataTypeFilter(payload));
    },
    [dispatch],
  );

  const handleClickOptions = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen]);

  const handleRepartitionChargeChange = useCallback(
    (checked) => {
      dispatch(changeFilters({ includeRepartitionCharge: checked }));
    },
    [dispatch],
  );

  const getDataTypeColor = useCallback((type) => {
    if (type === 'usage_data') return 'pie_2_0';
    if (type === 'aggregated_data' || type === 'temporary_data') return 'area_1';
    return 'area_0';
  }, []);

  const getDataTypeText = useCallback((type, lang) => {
    if (type === 'aggregated_data') return translations[lang].aggregated;
    if (type === 'usage_data') return translations[lang].usagedata;
    if (type === 'temporary_data') return translations[lang].trend_revenues;
    return translations[lang].earning;
  }, []);

  const getTooltipText = useCallback((type, lang) => {
    if (type === 'usage_data') return translations[lang]['tooltip.usage_data'];
    if (type === 'aggregated_data') return translations[lang]['tooltip.aggregated_data'];
    return translations[lang]['tooltip.confirmed_data'];
  }, []);

  const getHelpText = useCallback((type, lang) => {
    if (type === 'aggregated_data') return translations[lang].help_aggregated;
    if (type === 'temporary_data') return translations[lang].help_trend;
    return translations[lang].help_confirmed;
  }, []);

  return (
    <Wrapper isFixed={isFixed}>
      <div className="row">
        <div className="left">
          <ButtonWrapper>
            {!noShowCompare && formatComparedDates && (
              <div className="column">
                <div className="flex">
                  <Text
                    fontSize="h3"
                    lineHeight="h3"
                    fontWeight="bold"
                    mr="xs"
                    color={getDataTypeColor(dataType)}
                  >
                    {getDataTypeText(dataType, languageCode)}
                  </Text>

                  <MdHelpOutline data-tip data-for="help_data" size="1.2em" color="grey_400" />

                  {isAdminAnalytics && <MdOutlineAdminPanelSettings size="1.6em" color="red" />}
                </div>
                <OnlyWide>
                  <Text fontSize="label" lineHeight="label" fontWeight="normal" color="grey_400">
                    {`${translations[languageCode].period} ${moment(dates[0]).format(
                      'DD MMM YYYY',
                    )} - ${moment(dates[1]).format('DD MMM YYYY')} ${
                      translations[languageCode]['compared to']
                    } ${formatComparedDates.dateFrom} - ${formatComparedDates.dateTo}`}
                  </Text>
                </OnlyWide>
                {dataType !== 'usage_data' && (
                  <OnlyWide>
                    <Text fontSize="label" lineHeight="label" fontWeight="normal" color="grey_400">
                      {`${getHelpText(dataType, languageCode)}`}
                      {dataType !== 'usage_data' &&
                        !analytics_type?.match(/split/i) &&
                        analytics_type?.match(/enterprise/i) && (
                          <StyledLink
                            onClick={() =>
                              handleChangeDataType(
                                dataType === 'real_data' ? 'aggregated_data' : 'real_data',
                              )
                            }
                          >{`${
                            dataType === 'aggregated_data'
                              ? translations[languageCode].switchToConfirmed
                              : translations[languageCode].switchToAggregated
                          }`}</StyledLink>
                        )}
                    </Text>
                  </OnlyWide>
                )}
                <ToolTip
                  forData="help_data"
                  text={getTooltipText(dataType, languageCode)}
                  arrowColor="#098EA0"
                  backgroundColor="#098EA0"
                  place="right"
                  effect="solid"
                  html
                />
              </div>
            )}
          </ButtonWrapper>
        </div>

        <div className="right">
          <DateSelector
            noShowCompare={noShowCompare}
            dates={dates}
            selectByFilter={selectByFilter}
            languageCode={languageCode}
            dateRange={dateRange}
            dataType={dataType}
            compare={compare}
            direction="right"
          />

          <ButtonBorder
            onClick={handleOpenMoreFilters}
            style={{
              minWidth: '48px',
              height: '48px',
              p: 0,
              mr: 's',
              color: isOpenMoreFilters ? 'white' : 'primary_300',
              backgroundColor: isOpenMoreFilters ? 'primary_300' : 'transparent',
            }}
            type="button"
            label={
              <>
                <Tune /> <span style={{ marginLeft: '10px' }}>Filter results</span>
              </>
            }
            title={isOpenMoreFilters ? 'Close extra filters' : 'Open extra filters'}
          />

          <ButtonBorder
            onClick={handleClickOptions}
            style={{
              minWidth: '48px',
              height: '48px',
              p: 0,
              color: 'primary_300',
            }}
            type="button"
            label={<IconOptions />}
            title="Analytics options"
          />

          <Menu ref={ref} direction="right" bg="white" isVisible={isOpen} borderColor="primary_200">
            <ul>
              {!noReload && (
                <li>
                  <StyledBtnGhost
                    type="button"
                    onClick={() => {
                      handleReload();
                      setOpen(false);
                    }}
                  >
                    <Text
                      fontSize="pLarge"
                      lineHeight="pLarge"
                      fontWeight="pLarge"
                      color="grey_600"
                    >
                      <Reload /> {translations[languageCode]['Reload data']}
                    </Text>
                  </StyledBtnGhost>
                </li>
              )}
              {isAdminAnalytics && (
                <li>
                  <StyledDivGhost>
                    <Text
                      fontSize="pLarge"
                      lineHeight="pLarge"
                      fontWeight="pLarge"
                      color="grey_600"
                    >
                      <MdOutlineAdminPanelSettings />{' '}
                      {translations[languageCode]['Include Repartition Charges']}
                    </Text>
                    <Switch
                      checked={includeRepartitionCharge}
                      onChange={(checked) => {
                        handleRepartitionChargeChange(checked);
                        setOpen(false);
                      }}
                      size="small"
                      style={{ marginLeft: '10px' }}
                    />
                  </StyledDivGhost>
                </li>
              )}
              <li>
                <StyledBtnGhost
                  type="button"
                  onClick={() => {
                    handlePreset();
                    setOpen(false);
                  }}
                >
                  <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="pLarge" color="grey_600">
                    <RiListSettingsLine /> {translations[languageCode]['manage preset']}
                  </Text>
                </StyledBtnGhost>
              </li>
              <li>
                <StyledBtnGhost
                  type="button"
                  onClick={() => {
                    handleSavePreset();
                    setOpen(false);
                  }}
                >
                  <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="pLarge" color="grey_600">
                    <MdSave /> {translations[languageCode]['save preset']}
                  </Text>
                </StyledBtnGhost>
              </li>
            </ul>
          </Menu>
        </div>
      </div>
      <MoreFilters
        isOpen={isOpenMoreFilters}
        languageCode={languageCode}
        otherFilter={otherFilter}
        onChangeOtherFilter={handleChangeOtherFilter}
        onChangeTermFilter={handleChangeTermFilter}
        onReset={handleReset}
        dataType={dataType}
      />
      <FilterTags mt="s">
        <>
          {Object.keys(others).map((key) => (
            <Tag
              key={key}
              tagId={key}
              labelColor={others[key].color}
              labelTitle={translations[languageCode][others[key].type.split('.')[0]]}
              value={others[key].label}
              onDelete={handleDelete}
              isExclusive={others[key].isExclusive}
              onToggleExclusive={handleToggleExclusive}
            />
          ))}
          {Object.keys(terms).map((key) => (
            <Tag
              key={key}
              tagId={key}
              labelColor={terms[key].color}
              labelTitle={translations[languageCode][terms[key].type.split('.')[0]]}
              value={terms[key].label}
              onDelete={handleDeleteTerm}
              isExclusive={terms[key].isExclusive}
              onToggleExclusive={handleToggleExclusive}
            />
          ))}
        </>
      </FilterTags>

      <ManagePreset
        isVisible={isManagePresetVisible}
        onClose={handleCloseManagePreset}
        languageCode={languageCode}
      />

      <SavePreset
        isVisible={isSavePresetVisible}
        onClose={handleCloseSavePreset}
        languageCode={languageCode}
      />
    </Wrapper>
  );
}

export default Filters;
